import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, Trash2 } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { useAuthStore } from '@/stores/auth'
import {
  SELLER_NOTIFICATION_EVENTS,
  type Seller,
  type SellerResponse,
} from '@sdr-agend/admin-schemas'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

interface FormState {
  name: string
  phone: string
  email: string
  calendarId: string
  ghlUserId: string
  active: boolean
  notificationPrefs: Record<string, boolean>
}

const EMPTY_FORM: FormState = {
  name: '',
  phone: '',
  email: '',
  calendarId: '',
  ghlUserId: '',
  active: true,
  // Story 4.4 — events default to `true` (opt-in by default). The notifier
  // (`notifySellerEvent`) treats a missing key as opt-in too, so the form
  // stays consistent with the server-side default. Gerente unchecks an event
  // to skip + audit `notification.skipped_by_pref`.
  notificationPrefs: Object.fromEntries(
    SELLER_NOTIFICATION_EVENTS.map((e) => [e, true]),
  ) as Record<string, boolean>,
}

/**
 * E.164 quick-check used to disable Save when the phone is malformed.
 * Server still enforces the same regex via TypeBox (`Phone`).
 */
const E164_REGEX = /^\+\d{10,15}$/

/**
 * `/admin/sellers/:id` and `/admin/sellers/new`.
 *
 * RBAC:
 *   - admin / manager → full edit + create.
 *   - admin only → soft-delete (Excluir).
 *   - viewer / operator → read-only fields.
 */
export function SellerDetailPage(): React.ReactElement {
  const { id } = useParams<{ id?: string }>()
  const isNew = id === undefined || id === 'new'
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'admin'
  const canMutate = role === 'admin' || role === 'manager'

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })

  const { data, isLoading } = useQuery<SellerResponse>({
    queryKey: ['admin-sellers', id],
    queryFn: async () => api<SellerResponse>(`/admin/sellers/${id ?? ''}`),
    enabled: !isNew,
  })

  useEffect(() => {
    if (data?.seller !== undefined) {
      const s = data.seller
      // Story 4.4 — merge defaults (all `true`) with the seller's stored
      // prefs. A missing key (or null prefs) keeps the event as opt-in,
      // matching `notifySellerEvent`'s default. An explicit `false` from
      // the server flips the checkbox off.
      const stored = (s.notificationPrefs ?? {}) as Record<string, boolean>
      setForm({
        name: s.name,
        phone: s.phone,
        email: s.email ?? '',
        calendarId: s.calendarId ?? '',
        ghlUserId: s.ghlUserId ?? '',
        active: s.active,
        notificationPrefs: {
          ...EMPTY_FORM.notificationPrefs,
          ...stored,
        },
      })
    }
  }, [data])

  const seller: Seller | undefined = data?.seller

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }

  /**
   * Build the JSON payload sent to the API. Empty strings are converted to
   * omitted properties so the server keeps existing values on PATCH and
   * receives `undefined` for never-set optional fields on POST.
   */
  function buildPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      name: form.name,
      phone: form.phone,
      active: form.active,
      notificationPrefs: form.notificationPrefs,
    }
    if (form.email.trim() !== '') payload.email = form.email.trim()
    if (form.calendarId.trim() !== '') payload.calendarId = form.calendarId.trim()
    if (form.ghlUserId.trim() !== '') payload.ghlUserId = form.ghlUserId.trim()
    return payload
  }

  const createMutation = useMutation({
    mutationFn: async () =>
      api<SellerResponse>('/admin/sellers', {
        method: 'POST',
        body: JSON.stringify(buildPayload()),
      }),
    onSuccess: (res) => {
      notify({ variant: 'default', title: 'Vendedor criado', description: res.seller.name })
      void queryClient.invalidateQueries({ queryKey: ['admin-sellers'] })
      navigate(`/admin/sellers/${res.seller.id}`)
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao criar', description: msg })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async () =>
      api<SellerResponse>(`/admin/sellers/${id ?? ''}`, {
        method: 'PATCH',
        body: JSON.stringify(buildPayload()),
      }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Vendedor atualizado' })
      void queryClient.invalidateQueries({ queryKey: ['admin-sellers'] })
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao atualizar', description: msg })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () =>
      api<{ seller: Seller }>(`/admin/sellers/${id ?? ''}`, { method: 'DELETE' }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Vendedor desativado' })
      void queryClient.invalidateQueries({ queryKey: ['admin-sellers'] })
      navigate('/admin/sellers')
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao desativar', description: msg })
    },
  })

  // Calendar warning: an active seller without a calendar_id cannot receive
  // meetings. Story 3.6 §AC #5 surfaces a warning in the UI; the API still
  // accepts the row, so this is informational only.
  const showCalendarWarning =
    form.active && form.calendarId.trim() === '' && (isNew || data !== undefined)

  const phoneInvalid = form.phone !== '' && !E164_REGEX.test(form.phone)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label="Voltar">
            <Link to="/admin/sellers">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {isNew ? 'Novo Vendedor' : (seller?.name ?? '...')}
            </h1>
            {seller !== undefined && (
              <p className="text-sm text-muted-foreground">
                Atualizado em {new Date(seller.updatedAt).toLocaleString('pt-BR')}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!isNew && isAdmin && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Desativar
            </Button>
          )}
        </div>
      </div>

      {showCalendarWarning && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" aria-hidden="true" />
          <span>
            Vendedor ativo <strong>sem Calendar ID</strong> — agendamentos não serão criados até que
            você vincule um Google Calendar.
          </span>
        </div>
      )}

      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (isNew) createMutation.mutate()
          else updateMutation.mutate()
        }}
      >
        <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              disabled={!canMutate}
              required
              maxLength={200}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="phone">Telefone (E.164)</Label>
            <Input
              id="phone"
              value={form.phone}
              onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
              disabled={!canMutate}
              required
              placeholder="+5531991234567"
              aria-invalid={phoneInvalid}
            />
            {phoneInvalid && (
              <p className="text-xs text-destructive">
                Formato inválido. Use E.164 (ex: +5531991234567).
              </p>
            )}
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              disabled={!canMutate}
              placeholder="vendedor@empresa.com"
            />
          </div>
          <div className="flex items-center gap-2 pt-6">
            <input
              id="active"
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((s) => ({ ...s, active: e.target.checked }))}
              disabled={!canMutate}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="active" className="cursor-pointer">
              Ativo (recebe novos agendamentos)
            </Label>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
          <div className="grid gap-2">
            <Label htmlFor="calendarId">Calendar ID (Google)</Label>
            <Input
              id="calendarId"
              value={form.calendarId}
              onChange={(e) => setForm((s) => ({ ...s, calendarId: e.target.value }))}
              disabled={!canMutate}
              placeholder="vendedor@empresa.com"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="ghlUserId">GHL User ID</Label>
            <Input
              id="ghlUserId"
              value={form.ghlUserId}
              onChange={(e) => setForm((s) => ({ ...s, ghlUserId: e.target.value }))}
              disabled={!canMutate}
              placeholder="ghl_user_xxxxx"
            />
          </div>
        </div>

        <fieldset
          className="grid gap-2 rounded-md border p-4"
          aria-labelledby="notification-prefs-legend"
          data-testid="notification-prefs-section"
        >
          <legend id="notification-prefs-legend" className="px-1 text-sm font-medium">
            Notification Preferences
          </legend>
          <p className="text-xs text-muted-foreground">
            Marque os eventos para os quais o vendedor deve ser notificado.
            Eventos desmarcados são auditados como{' '}
            <code>notification.skipped_by_pref</code>.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {SELLER_NOTIFICATION_EVENTS.map((event) => (
              <label key={event} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.notificationPrefs[event] ?? true}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      notificationPrefs: {
                        ...s.notificationPrefs,
                        [event]: e.target.checked,
                      },
                    }))
                  }
                  disabled={!canMutate}
                  className="h-4 w-4 rounded border-input"
                  aria-label={`Notificação: ${event}`}
                />
                <span>{event}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {canMutate && (
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={
                createMutation.isPending ||
                updateMutation.isPending ||
                isLoading ||
                phoneInvalid
              }
            >
              {isNew ? 'Criar Vendedor' : 'Salvar Alterações'}
            </Button>
          </div>
        )}
      </form>

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && <ToastDescription>{toast.description}</ToastDescription>}
        </div>
      </Toast>
    </div>
  )
}

function formatApiError(err: ApiError): string {
  if (err.bodyJson !== null && typeof err.bodyJson === 'object' && 'error' in err.bodyJson) {
    const e = (err.bodyJson as { error: { code?: string; message?: string } }).error
    return e.message ?? e.code ?? err.message
  }
  return err.message
}

export default SellerDetailPage
